import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    input,
    InputSignal,
    output,
    OutputEmitterRef,
    Signal,
    signal,
    viewChild,
    WritableSignal
} from "@angular/core";
import {ResponsiveDirective} from "../../responsive/responsive.directive";
import {ResponsiveService} from "../../responsive/responsive.service";
import {IdentityModel} from '../../identity/identity.model';
import {LoyaltyCardData, LoyaltyCardType} from '../../identity/member-summary-entity.model';
import {mulberry32, span, toScale, xmur3} from '../../utils/string-utils';
import {NgClass} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';
import {IconComponent} from "../../ui-elements/icon/icon.component";
import {ImageCacheDirective} from '../../ui-elements/image/image-cache.directive';
import {LinkComponent} from '../../buttons/link/link.component';
import {CssVarDirective} from '../../utils/css-var.directive';
import {ElementWidthDirective} from '../../utils/element-width.directive';
import {ConfigurationModel} from '../../configuration/configuration.model';

@Component({
    selector: "woa-loyalty-card",
    templateUrl: "./loyalty-card.component.html",
    styleUrl: "./loyalty-card.component.scss",
    imports: [
        CssVarDirective,
        ElementWidthDirective,
        IconComponent,
        ImageCacheDirective,
        LinkComponent,
        NgClass,
        ResponsiveDirective,
        TranslateModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoyaltyCardComponent {
    private responsiveService: ResponsiveService = inject(ResponsiveService);
    public configurationModel: ConfigurationModel = inject(ConfigurationModel);
    public identityModel: IdentityModel = inject(IdentityModel);

    public readonly card: InputSignal<LoyaltyCardData> = input.required<LoyaltyCardData>();

    public readonly index: InputSignal<number> = input<number>();
    public readonly buttonMode: InputSignal<boolean> = input<boolean>(false);

    public onRedeem: OutputEmitterRef<void> = output<void>();

    private readonly content: Signal<ElementRef | undefined> = viewChild("content");
    private readonly stamps: Signal<ElementRef | undefined> = viewChild("stamps");

    private viewReady: WritableSignal<boolean> = signal(false);

    constructor() {
        afterNextRender(() => setTimeout(() => this.viewReady.set(true)));
    }

    LoyaltyCardType = LoyaltyCardType;
    toScale = toScale;

    public readonly stampRows: Signal<number> = computed<number>(() => {
        if (!this.viewReady() || !this.configurationModel.app() || this.responsiveService.windowWidth() === undefined || this.card()?.cardType !== LoyaltyCardType.STAMPS || !this.content()?.nativeElement) {
            return 0;
        }
        const spec: string = getComputedStyle(this.content().nativeElement).getPropertyValue("--themed-loyalty-card-max-column-spec").trim();
        const maxCols: number[] = spec.split(/[, ]/).map((s) => parseInt(s.trim()));
        for (let rows: number = 1; rows <= maxCols.length; rows++) {
            if (this.card().goal <= (rows * maxCols[rows - 1])) {
                return rows;
            }
        }
        return 0;
    });

    public readonly stampColumns: Signal<number> = computed<number>(() =>
        (!this.configurationModel.app() ||
            this.responsiveService.windowWidth() === undefined ||
            this.card()?.cardType !== LoyaltyCardType.STAMPS) ? 0 : Math.ceil(this.card().goal / this.stampRows()));

    public readonly stampPlaceholderContainerSize: Signal<number> = computed<number>(() => {
        if (this.card()?.cardType !== LoyaltyCardType.STAMPS || !this.stamps()?.nativeElement || !this.content()?.nativeElement) {
            return 0;
        }
        const cellHeight: number = Math.floor(this.stamps().nativeElement.offsetHeight / this.stampRows());
        const cellWidth: number = Math.floor(this.stamps().nativeElement.offsetWidth / this.stampColumns());
        return Math.min(cellHeight, cellWidth, 65);
    });

    public readonly stampIcon: Signal<string> = computed(() =>
        (this.card()?.cardType !== LoyaltyCardType.STAMPS ||
            !this.content()?.nativeElement) ? "" : getComputedStyle(this.content().nativeElement).getPropertyValue("--themed-loyalty-card-stamp-icon").trim());

    public readonly stampRotation: Signal<(stampNr: number) => string> = computed<(stampNr: number) => string>(() => {
        const stampName: (stampNr: number) => string = this.stampName();
        return (stampNr: number): string => {
            const seedFn: () => number = xmur3(stampName(stampNr));
            const rand: () => number = mulberry32(seedFn());
            return (rand() * 360 - 180) + "deg";
        }
    });

    public readonly stampName: Signal<(stampNr: number) => string> = computed<(stampNr: number) => string>(() => {
        const cardName: string = this.card().name;
        return (stampNr: number): string => cardName + "::" + stampNr;
    });

    public readonly cells: Signal<Array<number>> = computed<Array<number>>(() => Array.from(Array(this.card().goal), (_, x) => x));
    public readonly cardTheme: Signal<string> = computed(() => this.card().theme ? this.card().theme.replace(" ", "_") : "default");
    public readonly stampsRequired: Signal<number> = computed<number>(() => this.card().goal - this.card().purchaseCount);
    public readonly pointsRequired: Signal<string> = computed(() => toScale(Math.max(0, this.card().pointsRequiredToRedeem - this.identityModel.currentMember().pointsBalance), 4));

    public readonly footerText: Signal<string> = computed(() =>
        this.card().cardType === LoyaltyCardType.STAMPS ? "woa.message.loyalty-card.stampsRequiredText"
            : this.card().pointsRequiredToRedeem > this.identityModel.currentMember().pointsBalance ? "woa.message.loyalty-card.pointsRequiredText"
                : "woa.message.loyalty-card.redeemPointsText");
    protected readonly span = span;
}
